import React, { useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Link,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Checkbox as MuiCheckbox,
  Snackbar,
} from '@mui/material';
import { Visibility, VisibilityOff, Person, Email, Lock } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { register as registerUser, clearError } from '../../store/slices/authSlice';
import { RegisterDto } from '../../../domain/entities';
import { ROUTES } from '../../../core/config/constants';

const schema = yup.object({
  firstName: yup.string().required('Prénom requis'),
  lastName: yup.string().required('Nom requis'),
  mail: yup.string().email('Email invalide').required('Email requis'),
  password: yup
    .string()
    .min(6, 'Le mot de passe doit contenir au moins 6 caractères')
    .required('Mot de passe requis'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password')], 'Les mots de passe ne correspondent pas')
    .required('Confirmation requise'),
});

export const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isLoading, error, isAuthenticated } = useAppSelector((state) => state.auth);
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [acceptTerms, setAcceptTerms] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterDto>({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    // Ne pas rediriger automatiquement après l'inscription
    // pour permettre l'affichage de la popup de succès
  }, []);

  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const onSubmit = async (data: RegisterDto) => {
    const result = await dispatch(registerUser(data));
    if (registerUser.fulfilled.match(result)) {
      setSuccessMessage(true);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#000000',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 3,
      }}
    >
      {/* Logo Vinosk */}
      <Box
        component="img"
        src="https://vinosk.fr/images/logo-white.png"
        alt="Vinosk Logo"
        sx={{
          width: 100,
          height: 100,
          mb: 3,
        }}
      />

      {/* Titre */}
      <Typography
        variant="h4"
        sx={{
          color: 'white',
          fontWeight: 600,
          mb: 2,
          fontSize: '1.8rem',
        }}
      >
        Créer un compte
      </Typography>

      {/* Sous-titre */}
      <Typography
        variant="body1"
        sx={{
          color: 'rgba(255, 255, 255, 0.7)',
          mb: 4,
          textAlign: 'center',
        }}
      >
        Rejoignez Vinosk pour gérer votre cave
      </Typography>

      {/* Messages d'erreur */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, backgroundColor: '#8B2F54', color: 'white', width: '100%', maxWidth: 400 }}>
          {error}
        </Alert>
      )}

      {/* Formulaire */}
      <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ width: '100%', maxWidth: 400 }}>
        
        {/* Prénom et Nom sur la même ligne */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <TextField
            {...register('firstName')}
            fullWidth
            placeholder="Prénom"
            name="firstName"
            autoComplete="given-name"
            autoFocus
            error={!!errors.firstName}
            helperText={errors.firstName?.message}
            disabled={isLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person sx={{ color: '#8B2F54' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#333',
                borderRadius: 2,
                color: 'white',
                '& fieldset': {
                  borderColor: 'transparent',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#8B2F54',
                },
                '& input': {
                  color: 'white',
                  '&::placeholder': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    opacity: 1,
                  },
                },
              },
              '& .MuiFormHelperText-root': {
                color: '#ff6b6b',
              },
            }}
          />
          <TextField
            {...register('lastName')}
            fullWidth
            placeholder="Nom"
            name="lastName"
            autoComplete="family-name"
            error={!!errors.lastName}
            helperText={errors.lastName?.message}
            disabled={isLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person sx={{ color: '#8B2F54' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#333',
                borderRadius: 2,
                color: 'white',
                '& fieldset': {
                  borderColor: 'transparent',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#8B2F54',
                },
                '& input': {
                  color: 'white',
                  '&::placeholder': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    opacity: 1,
                  },
                },
              },
              '& .MuiFormHelperText-root': {
                color: '#ff6b6b',
              },
            }}
          />
        </Box>

        {/* Email */}
        <TextField
          {...register('mail')}
          fullWidth
          placeholder="Email"
          name="mail"
          autoComplete="email"
          error={!!errors.mail}
          helperText={errors.mail?.message}
          disabled={isLoading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Email sx={{ color: '#8B2F54' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#333',
              borderRadius: 2,
              color: 'white',
              '& fieldset': {
                borderColor: 'transparent',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#8B2F54',
              },
              '& input': {
                color: 'white',
                '&::placeholder': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  opacity: 1,
                },
              },
            },
            '& .MuiFormHelperText-root': {
              color: '#ff6b6b',
            },
          }}
        />

        {/* Mot de passe */}
        <TextField
          {...register('password')}
          fullWidth
          placeholder="Mot de passe"
          name="password"
          type={showPassword ? 'text' : 'password'}
          autoComplete="new-password"
          error={!!errors.password}
          helperText={errors.password?.message}
          disabled={isLoading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Lock sx={{ color: '#8B2F54' }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleClickShowPassword}
                  edge="end"
                  sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#333',
              borderRadius: 2,
              color: 'white',
              '& fieldset': {
                borderColor: 'transparent',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#8B2F54',
              },
              '& input': {
                color: 'white',
                '&::placeholder': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  opacity: 1,
                },
              },
            },
            '& .MuiFormHelperText-root': {
              color: '#ff6b6b',
            },
          }}
        />

        {/* Confirmer mot de passe */}
        <TextField
          {...register('confirmPassword')}
          fullWidth
          placeholder="Confirmer le mot de passe"
          name="confirmPassword"
          type={showConfirmPassword ? 'text' : 'password'}
          autoComplete="new-password"
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword?.message}
          disabled={isLoading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Lock sx={{ color: '#8B2F54' }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleClickShowConfirmPassword}
                  edge="end"
                  sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 4,
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#333',
              borderRadius: 2,
              color: 'white',
              '& fieldset': {
                borderColor: 'transparent',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#8B2F54',
              },
              '& input': {
                color: 'white',
                '&::placeholder': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  opacity: 1,
                },
              },
            },
            '& .MuiFormHelperText-root': {
              color: '#ff6b6b',
            },
          }}
        />

        {/* Conditions d'utilisation */}
        <FormControlLabel
          control={
            <MuiCheckbox
              checked={acceptTerms}
              onChange={(e) => setAcceptTerms(e.target.checked)}
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                '&.Mui-checked': {
                  color: '#8B2F54',
                },
                padding: '9px',
              }}
            />
          }
          label={
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              J'accepte les{' '}
              <Link href="#" sx={{ color: '#8B2F54', textDecoration: 'underline' }}>
                conditions générales d'utilisation
              </Link>
            </Typography>
          }
          sx={{ 
            mb: 6, 
            alignItems: 'center',
            margin: 0,
            '& .MuiFormControlLabel-label': {
              paddingLeft: '8px'
            }
          }}
        />

        {/* Bouton S'inscrire */}
        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={isLoading || !acceptTerms}
          sx={{
            backgroundColor: '#8B2F54',
            color: 'white',
            py: 2,
            fontSize: '1.1rem',
            fontWeight: 500,
            borderRadius: 2,
            mb: 4,
            '&:hover': {
              backgroundColor: '#A03965',
            },
            '&:disabled': {
              backgroundColor: '#666',
              color: '#ccc',
            },
          }}
        >
          {isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'S\'inscrire'
          )}
        </Button>

        {/* Lien de connexion */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Déjà un compte ?{' '}
            <Link
              component={RouterLink}
              to={ROUTES.LOGIN}
              sx={{
                color: '#8B2F54',
                textDecoration: 'none',
                fontWeight: 500,
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Se connecter
            </Link>
          </Typography>
        </Box>
      </Box>

      {/* Success Snackbar */}
      <Snackbar
        open={successMessage}
        onClose={() => setSuccessMessage(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          '& .MuiPaper-root': {
            top: '50%',
            transform: 'translateY(-50%)',
          }
        }}
      >
        <Alert
          onClose={() => setSuccessMessage(false)}
          severity="success"
          icon={false}
          sx={{ 
            backgroundColor: '#1A1A1A',
            border: '2px solid #8B2F54',
            color: 'white',
            fontSize: '1rem',
            fontWeight: 500,
            padding: '40px',
            borderRadius: 2,
            boxShadow: '0 12px 40px rgba(139, 47, 84, 0.3)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            gap: 2,
            maxWidth: 450,
            width: '100%',
            '& .MuiAlert-action': {
              display: 'none'
            }
          }}
        >
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700, 
              mb: 2,
              color: '#8B2F54',
              letterSpacing: '0.5px'
            }}
          >
            Félicitations !
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              mb: 2,
              color: '#FFFFFF',
              fontSize: '1.1rem'
            }}
          >
            Votre compte a été créé avec succès.
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              mb: 4,
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '0.95rem',
              lineHeight: 1.6
            }}
          >
            Veuillez vous rendre sur votre boite mail pour activer votre compte.
          </Typography>
          <Button
            variant="contained"
            fullWidth
            onClick={() => navigate(ROUTES.LOGIN)}
            sx={{
              backgroundColor: '#8B2F54',
              color: 'white',
              fontWeight: 600,
              py: 2,
              fontSize: '1.1rem',
              borderRadius: 2,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#A03965',
              }
            }}
          >
            Fermer
          </Button>
        </Alert>
      </Snackbar>
    </Box>
  );
};